// native-bridge.ts
export type Resolver<T> = (value: T) => void;

export function awaitNativeOnce<T>(opts: {
    setResolver: (resolver: Resolver<T>) => void;
    clearResolver: () => void;
    invokeNative: () => void;
    timeoutValue: T;
    timeoutMs?: number;

    /** Optional label to identify the call in logs */
    name?: string;

    /** Enable/disable logs */
    debug?: boolean;
}): Promise<T> {
    const timeoutMs = opts.timeoutMs ?? 5000;
    const debug = opts.debug ?? false;
    const name = opts.name ?? "awaitNativeOnce";

    const log = (...args: any[]) => {
        if (!debug) return;
        try {
            console.log(`[${name}]`, ...args);
        } catch {
            // ignore logging errors
        }
    };

    const warn = (...args: any[]) => {
        if (!debug) return;
        try {
            console.warn(`[${name}]`, ...args);
        } catch {
            // ignore logging errors
        }
    };

    return new Promise<T>((resolve) => {
        let done = false;

        const finish = (value: T, reason: "resolver" | "timeout" | "invoke-throw") => {
            if (done) return;
            done = true;

            log(`Finishing via ${reason}.`);

            try {
                opts.clearResolver();
                log("Resolver cleared.");
            } catch (err) {
                warn("Error clearing resolver:", err);
            }

            resolve(value);
        };

        // Install the resolver (native will call this later)
        try {
            opts.setResolver((value: T) => {
                log("Resolver invoked with value:", value);
                finish(value, "resolver");
            });

            log("Resolver installed.");
        } catch (err) {
            warn("Error installing resolver:", err);
            finish(opts.timeoutValue, "invoke-throw");
            return;
        }

        // Kick off native call
        try {
            log("Invoking native call...");
            opts.invokeNative();
            log("Native call invoked successfully.");
        } catch (err) {
            warn("Native invoke threw error:", err);
            finish(opts.timeoutValue, "invoke-throw");
            return;
        }

        // Timeout fallback
        setTimeout(() => {
            warn(`Timed out after ${timeoutMs}ms.`);
            finish(opts.timeoutValue, "timeout");
        }, timeoutMs);
    });
}
